/** Lyric prompter timing — sung lines spread across the song, longer lines get more time. */
const LABEL = /^\s*[[(].*[\])]\s*$|^\s*(verse|chorus|bridge|intro|outro|pre-chorus|hook|tag|vamp|refrain|interlude)\b[\s\d:.-]*$/i;

export function parseLyrics(text) {
  return (text || "").split(/\r?\n/).map((t) => t.trim()).filter(Boolean).map((t) => ({ text: t, label: LABEL.test(t) }));
}

/** → [{ text, label, start, end }]; section labels take the start time of the next sung line.
 *  With a tempo, every sung line starts on a bar (4 beats; single beats when the lines outnumber the bars). */
export function timeLines(lines, start, end, tempo) {
  const weights = lines.filter((l) => !l.label).map((l) => Math.max(12, l.text.length));
  if (tempo?.bpm && weights.length) return onTheBeat(lines, weights, start, end, tempo);
  const total = weights.reduce((a, b) => a + b, 0) || 1;
  const span = Math.max(1, end - start);
  let t = start;
  let k = 0;
  return lines.map((l) => {
    if (l.label) return { ...l, start: t, end: t };
    const d = (weights[k] / total) * span;
    k += 1;
    const out = { ...l, start: t, end: t + d };
    t += d;
    return out;
  });
}

function onTheBeat(lines, weights, start, end, tempo) {
  const beat = 60 / tempo.bpm;
  let unit = beat * 4;
  const first = (u) => tempo.offset + Math.round((start - tempo.offset) / u) * u;
  if (Math.floor((end - first(unit)) / unit) < weights.length) unit = beat;
  const t0 = Math.max(0, first(unit));
  const slots = Math.max(weights.length, Math.floor((end - t0) / unit));
  const total = weights.reduce((a, b) => a + b, 0);
  const starts = [];
  let acc = 0;
  weights.forEach((w) => {
    const slot = Math.max(starts.length ? starts[starts.length - 1] + 1 : 0, Math.round((acc / total) * slots));
    starts.push(Math.min(slot, slots - (weights.length - starts.length)));
    acc += w;
  });
  let k = 0;
  let t = t0;
  return lines.map((l) => {
    if (l.label) return { ...l, start: t, end: t };
    const s = t0 + starts[k] * unit;
    const e = t0 + (k + 1 < starts.length ? starts[k + 1] : slots) * unit;
    k += 1;
    t = e;
    return { ...l, start: s, end: e };
  });
}

/** Index of the sung line at `time` (−1 before the first, last index after the end). */
export function lineAt(timed, time) {
  const sung = timed.map((l, i) => ({ l, i })).filter(({ l }) => !l.label);
  if (!sung.length || time < sung[0].l.start) return -1;
  const hit = sung.find(({ l }) => time >= l.start && time < l.end);
  return hit ? hit.i : sung[sung.length - 1].i;
}
